/**
 * SmartAvatar - Componente Avatar Inteligente
 * 
 * Encapsula automaticamente:
 * - Conversão de caminhos relativos para URL completa via getStorageUrl
 * - Suporte a IDs de avatar 3D (ex: "avatar-5")
 * - Geração de iniciais para fallback
 * - Cores automáticas estilo WhatsApp Business
 * - Estilos consistentes com variantes de tamanho
 * - Suporte a avatares 3D para usuários/vendedores (use3DFallback)
 * 
 * Versão: 1.3.0
 * - Adicionado suporte para detectar IDs de avatar 3D no src
 */

import * as React from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { getStorageUrl } from "@/lib/storage";
import { getInitials } from "@/lib/utils/initials";
import { getAvatarColorClasses } from "@/lib/utils/avatar-colors";
import { get3DAvatarUrl, get3DAvatarById, is3DAvatarId } from "@/lib/utils/avatar-3d";
import { cn } from "@/lib/utils";

// Variantes de tamanho pré-definidas
const sizeVariants = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-16 w-16 text-lg",
  "2xl": "h-20 w-20 text-xl",
  "3xl": "h-24 w-24 text-2xl",
};

export interface SmartAvatarProps {
  /** 
   * Caminho relativo do storage, URL completa, ou ID de avatar 3D (ex: "avatar-5")
   * Se for um ID de avatar 3D, será convertido automaticamente para a URL do avatar
   */
  src?: string | null;
  /** Nome para gerar iniciais no fallback */
  name?: string | null;
  /** Email alternativo para iniciais (se name não disponível) */
  email?: string | null;
  /** Tamanho do avatar */
  size?: keyof typeof sizeVariants;
  /** Fallback customizado (se não quiser iniciais) */
  fallback?: React.ReactNode;
  /** Fallback padrão quando não há nome/email */
  defaultFallback?: string;
  /** Classes adicionais para o container */
  className?: string;
  /** Classes para o fallback */
  fallbackClassName?: string;
  /** Alt text para acessibilidade */
  alt?: string;
  /** Desabilita cores automáticas no fallback */
  disableAutoColor?: boolean;
  /** 
   * Usar avatar 3D como fallback (para usuários/vendedores)
   * Hierarquia: foto do usuário → avatar 3D → iniciais coloridas
   */
  use3DFallback?: boolean;
  /** Handler de clique no avatar (ex: abrir lightbox) */
  onClick?: () => void;
}

/**
 * Avatar inteligente com conversão automática de URL, iniciais e cores
 * Suporta avatares 3D para usuários do sistema e vendedores
 */
export const SmartAvatar = React.forwardRef<HTMLDivElement, SmartAvatarProps>(
  (
    {
      src,
      name,
      email,
      size = "md",
      fallback,
      defaultFallback = "?",
      className,
      fallbackClassName,
      alt,
      disableAutoColor = false,
      use3DFallback = false,
      onClick,
    },
    ref
  ) => {
    // Estado para controlar se a imagem principal falhou
    const [imageError, setImageError] = React.useState(false);
    
    // Verifica se o src é um ID de avatar 3D (ex: "avatar-5")
    const isSrc3DAvatar = is3DAvatarId(src);
    
    // Converte caminho para URL:
    // - Se for ID de avatar 3D, busca a URL do avatar
    // - Senão, converte caminho de storage para URL completa
    const imageUrl = React.useMemo(() => {
      if (!src) return null;
      
      // Se for ID de avatar 3D, retorna URL do avatar
      if (isSrc3DAvatar) {
        return get3DAvatarById(src);
      }
      
      // Senão, trata como path de storage
      return getStorageUrl(src);
    }, [src, isSrc3DAvatar]);
    
    // URL do avatar 3D (usado como segundo fallback se use3DFallback=true e não é ID de avatar)
    const avatar3DUrl = use3DFallback && !isSrc3DAvatar ? get3DAvatarUrl(name || email) : null;
    
    // Gera iniciais do nome ou email
    const initials = getInitials(name || email, defaultFallback);
    
    // Gera classes de cor baseadas no nome (estilo WhatsApp Business)
    const autoColorClasses = !disableAutoColor 
      ? getAvatarColorClasses(name || email) 
      : '';
    
    // Reset do estado de erro quando src muda
    React.useEffect(() => {
      setImageError(false);
    }, [src]);
    
    // Determina qual imagem mostrar baseado na hierarquia de fallback
    // 1. Foto do usuário (src)
    // 2. Avatar 3D (se use3DFallback=true e não tem foto)
    // 3. Iniciais coloridas (fallback final)
    const showAvatar3D = use3DFallback && (!imageUrl || imageError) && avatar3DUrl;
    
    return (
      <Avatar
        ref={ref}
        className={cn(sizeVariants[size], "shrink-0", onClick && "cursor-pointer", className)}
        onClick={onClick}
      >
        {/* Imagem principal do usuário */}
        {imageUrl && !imageError && (
          <AvatarImage 
            src={imageUrl} 
            alt={alt || name || "Avatar"}
            onError={() => setImageError(true)}
          />
        )}
        
        {/* Avatar 3D como segundo fallback */}
        {showAvatar3D && (
          <AvatarImage 
            src={avatar3DUrl} 
            alt={alt || name || "Avatar 3D"}
          />
        )}
        
        {/* Fallback final: iniciais coloridas */}
        <AvatarFallback 
          className={cn(
            "font-medium",
            autoColorClasses,
            fallbackClassName
          )}
        >
          {fallback || initials}
        </AvatarFallback>
      </Avatar>
    );
  }
);

SmartAvatar.displayName = "SmartAvatar";
